import { parseAsBoolean, parseAsString, parseAsStringLiteral } from 'nuqs';
import { flowUrlParams } from '../_shared/url-params';
import { ScanStatusSchema, type Scan, type ScanStatus } from './scan';

/**
 * URL state for the unified Scans page (plan
 * plans/2026-08-26-001-lead-scanner-scans-page-plan.md). Every filter AND the
 * currently-open scan live in the URL (`docs/flow/unified-shell-and-
 * deeplinking.md` §4's "any state a rep would expect to survive a tab switch
 * or share in Slack must live in the URL") so a rep can text a teammate a
 * link that opens one specific scan directly.
 *
 * Param vocabulary: `campaign` (not `event`) per the deep-linking standard's
 * canonical vocabulary (§4) — `?campaign=` is reserved across every Flow app
 * for a campaign id.
 *
 * Kept as plain, framework-free functions (this file imports no React and no
 * Glaze) so `applyScanFilters`/`linkedScanFilterClears` are directly
 * unit-testable under `bun test` — there is no React-DOM test harness under
 * `mini-apps/` for this app to lean on instead.
 */

export const scansPageParams = {
  /** The open scan's id, absent = none. */
  scan: parseAsString,
  /** Default "my scans only" — invisible in the URL at its default; only
   *  `?mine=false` (an explicit "Everyone" clear) ever appears. */
  mine: parseAsBoolean.withDefault(true).withOptions({ clearOnDefault: true }),
  /** No default = all statuses. */
  status: parseAsStringLiteral(ScanStatusSchema.options),
  /** Explicit submitter email filter — overrides `mine` when set. */
  rep: parseAsString,
  /** Campaign id filter. */
  campaign: flowUrlParams.campaign,
  /** Free-text search over person name / company / event name — invisible in
   *  the URL at its default (empty), same `clearOnDefault` pattern as `mine`. */
  q: flowUrlParams.q.withDefault('').withOptions({ clearOnDefault: true }),
};

export interface ScansFilters {
  mine: boolean;
  rep: string | null;
  status: ScanStatus | null;
  campaign: string | null;
  q: string;
}

/**
 * The submitter a "mine"/"rep" filter pair resolves to. `rep` (an explicit
 * pick) always wins over `mine`; `null` means "everyone" — no submitter
 * filter at all.
 */
export function effectiveSubmitter(mine: boolean, rep: string | null, me: string): string | null {
  return rep ?? (mine ? me : null);
}

/**
 * Filters the fetched scans list by the current URL filter state.
 *
 * `campaignNames` (id → name) is optional so existing callers/tests that only
 * exercise the submitter/status/campaign filters can omit it — a `q` search
 * still matches against `name`/`company` without it, it just can't match the
 * campaign/event name.
 */
export function applyScanFilters(
  scans: Scan[],
  filters: ScansFilters,
  me: string,
  campaignNames?: Map<string, string>,
): Scan[] {
  const submitter = effectiveSubmitter(filters.mine, filters.rep, me);
  const q = filters.q.trim().toLowerCase();
  return scans.filter((scan) => {
    if (submitter !== null && scan.scannedByEmail !== submitter) return false;
    if (filters.status !== null && scan.status !== filters.status) return false;
    if (filters.campaign !== null && scan.campaignId !== filters.campaign) return false;
    if (q) {
      const haystack = `${scan.name} ${scan.company} ${campaignNames?.get(scan.campaignId) ?? ''}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });
}

/** The minimal filter overrides `linkedScanFilterClears` can hand back to a
 *  `useQueryStates` setter to make a linked scan visible. */
export type ScanFilterClears = Partial<{
  mine: boolean;
  rep: string | null;
  status: null;
  campaign: null;
  q: string;
}>;

/**
 * "An explicit link always wins" (the Scans page's deep-link contract): given
 * the scan a `?scan=<id>` param resolved to and the CURRENT filter state,
 * returns the minimal set of filter overrides needed to make that scan
 * visible — or `null` if it's already visible and nothing needs to change.
 *
 * Never touches `scan` itself — this only ever relaxes filters, it never
 * mutates or re-derives scan data. `campaignNames` is optional for the same
 * reason as `applyScanFilters` — an omitted map just means a `q` search can
 * only be resolved against `name`/`company`.
 */
export function linkedScanFilterClears(
  scan: Scan,
  filters: ScansFilters,
  me: string,
  campaignNames?: Map<string, string>,
): ScanFilterClears | null {
  const clears: ScanFilterClears = {};

  const submitter = effectiveSubmitter(filters.mine, filters.rep, me);
  if (submitter !== null && scan.scannedByEmail !== submitter) {
    clears.mine = false;
    clears.rep = null;
  }
  if (filters.status !== null && scan.status !== filters.status) {
    clears.status = null;
  }
  if (filters.campaign !== null && scan.campaignId !== filters.campaign) {
    clears.campaign = null;
  }
  const q = filters.q.trim().toLowerCase();
  if (q) {
    const haystack = `${scan.name} ${scan.company} ${campaignNames?.get(scan.campaignId) ?? ''}`.toLowerCase();
    if (!haystack.includes(q)) {
      clears.q = '';
    }
  }

  return Object.keys(clears).length > 0 ? clears : null;
}
